import React from 'react';
import { Card, CardContent } from '../../ui/card';
import { Flame, Beef, Wheat, Droplet, Target, Footprints } from 'lucide-react';

// Ring/Circle Progress component
function RingProgress({ 
  value, 
  max, 
  size = 100, 
  strokeWidth = 8, 
  color = '#22c55e',
  bgColor = 'rgba(100, 116, 139, 0.3)',
  label,
  sublabel 
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percent = Math.min((value / max) * 100, 100);
  const strokeDashoffset = circumference - (percent / 100) * circumference;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={bgColor}
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-white font-bold" style={{ fontSize: size * 0.22 }}>{label}</span>
        {sublabel && (
          <span className="text-slate-400" style={{ fontSize: size * 0.12 }}>{sublabel}</span>
        )}
      </div>
    </div>
  );
}

export function MacrosSummary({
  entries,
  goals,
  burnedCalories,
  externalSteps,
  bmrData,
  bodyWeight,
  onEditGoals,
}) {
  // Calculate totals
  const totals = entries.reduce(
    (acc, entry) => ({
      calories: acc.calories + (entry.calories || 0),
      protein: acc.protein + (entry.protein || 0),
      carbs: acc.carbs + (entry.carbs || 0),
      fat: acc.fat + (entry.fat || 0),
    }),
    { calories: 0, protein: 0, carbs: 0, fat: 0 }
  );

  // Calculate total activity calories
  const totalActivityCalories = burnedCalories + (bmrData?.workoutCalories || 0);
  const walkCalories = bmrData?.walkCalories || 0;

  // Daily allowance based on BMR or goals
  const dailyAllowance = bmrData?.dailyTarget || goals.calories;
  
  // Deficit calculation
  const deficit = totals.calories - dailyAllowance;

  // Goal type
  const goalType = bmrData?.goal || 'maintain';
  const goalLabel = goalType === 'lose' ? 'Lose Weight' : 'Maintain';

  // Show cycle info for lose weight
  const cycleWeek = bmrData?.cycleWeek || 0;
  const cyclePhase = bmrData?.cyclePhase || '';
  const calorieAdjustment = bmrData?.calorieAdjustment || 0;

  return (
    <div className="space-y-3">
      {/* Main Calories Card with Ring Diagram */}
      <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            {/* Left side - Stats */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-orange-500"></div>
                <span className="text-slate-400 text-sm">Burned</span>
                <span className="text-green-400 text-sm ml-auto">+{totalActivityCalories + walkCalories}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                <span className="text-slate-400 text-sm">Consumed</span>
                <span className="text-white text-sm font-semibold ml-auto">{totals.calories}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                <span className="text-slate-400 text-sm">Allowance</span>
                <span className="text-cyan-400 text-sm ml-auto">{dailyAllowance}</span>
              </div>
            </div>
            
            {/* Right side - Ring Diagram */}
            <div className="flex flex-col items-center">
              <RingProgress
                value={totals.calories}
                max={dailyAllowance}
                size={110}
                strokeWidth={10}
                color={totals.calories > dailyAllowance ? '#ef4444' : '#22c55e'}
                label={totals.calories}
                sublabel={`/ ${dailyAllowance}`}
              />
            </div>
          </div>
          
          {/* Goal and Deficit Row */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-2.5 text-center">
              <span className="text-slate-400 text-xs">Goal</span>
              <p className={`font-bold text-sm ${goalType === 'lose' ? 'text-red-400' : 'text-green-400'}`}>{goalLabel}</p>
              {goalType === 'lose' && cycleWeek > 0 && (
                <>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    W{cycleWeek}/20 {cyclePhase} ({calorieAdjustment})
                  </p>
                  {/* Cycle progress bar */}
                  <div className="mt-1.5 flex gap-[1px]" data-testid="cycle-progress-bar">
                    {Array.from({ length: 20 }, (_, i) => {
                      const week = i + 1;
                      const isCurrentWeek = week === cycleWeek;
                      let color;
                      if (week <= 2) color = 'bg-orange-400'; // gentle start
                      else if (week <= 10) color = 'bg-red-400'; // full deficit
                      else if (week <= 12) color = 'bg-orange-400'; // taper
                      else color = 'bg-green-400'; // maintain
                      
                      return (
                        <div
                          key={week}
                          className={`h-1.5 flex-1 rounded-[1px] transition-all ${
                            isCurrentWeek 
                              ? `${color} ring-1 ring-white ring-offset-1 ring-offset-slate-800 h-2.5 -mt-0.5`
                              : week < cycleWeek ? `${color} opacity-80` : `${color} opacity-25`
                          }`}
                          title={`W${week}: ${week <= 2 ? '-250' : week <= 10 ? '-500' : week <= 12 ? '-250' : '0'}`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-0.5">
                    <span className="text-[8px] text-slate-600">Lose</span>
                    <span className="text-[8px] text-slate-600">Maintain</span>
                  </div>
                </>
              )}
            </div>
            <div className="bg-slate-800/50 border border-teal-800/50 rounded-xl p-2.5 text-center">
              <span className="text-slate-400 text-xs">Deficit</span>
              <p className={`font-bold text-sm ${deficit < 0 ? 'text-green-400' : 'text-red-400'}`}>
                {deficit > 0 ? '+' : ''}{deficit}
              </p>
            </div>
          </div>
          
          {/* Total Daily Allowance Footer */}
          <div className="mt-3 pt-3 border-t border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-green-400 text-sm font-medium">Daily Target</span>
                <p className="text-slate-500 text-xs">
                  TDEE {bmrData?.tdee || '—'}{calorieAdjustment ? ` ${calorieAdjustment}` : ''} + Walk {walkCalories}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400 font-bold text-lg">{dailyAllowance} kcal</span>
                <button
                  onClick={onEditGoals}
                  className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <Target className="h-4 w-4 text-slate-400" />
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Macros Ring Diagrams */}
      <Card className="bg-slate-900/80 border-slate-800 rounded-2xl">
        <CardContent className="p-4">
          <div className="flex items-center justify-around">
            {/* Protein Ring */}
            <div className="flex flex-col items-center">
              <RingProgress
                value={totals.protein}
                max={goals.protein}
                size={80}
                strokeWidth={7}
                color="#ef4444"
                label={`${Math.round(totals.protein)}g`}
              />
              <span className="text-slate-500 text-xs mt-1">/ {goals.protein}g</span>
              <span className="text-red-400 text-xs font-semibold">Protein</span>
            </div>
            
            {/* Carbs Ring */}
            <div className="flex flex-col items-center">
              <RingProgress
                value={totals.carbs}
                max={goals.carbs}
                size={80}
                strokeWidth={7}
                color="#eab308"
                label={`${Math.round(totals.carbs)}g`}
              />
              <span className="text-slate-500 text-xs mt-1">/ {goals.carbs}g</span>
              <span className="text-yellow-400 text-xs font-semibold">Carbs</span>
            </div>
            
            {/* Fat Ring */}
            <div className="flex flex-col items-center">
              <RingProgress
                value={totals.fat}
                max={goals.fat}
                size={80}
                strokeWidth={7}
                color="#3b82f6"
                label={`${Math.round(totals.fat)}g`}
              />
              <span className="text-slate-500 text-xs mt-1">/ {goals.fat}g</span>
              <span className="text-blue-400 text-xs font-semibold">Fat</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default MacrosSummary;
